import { devLogger } from '@libs/fsc/utils/logger'

import AuthWorkerConstructor from './authWorker?worker'

// ***
// TYPES
// ***
type WorkerMessage = { type: string; [key: string]: unknown }
type Handler = (data: WorkerMessage) => void

const listeners: { type: string; handler: Handler }[] = []

export function addAuthWorkerListener(type: string, handler: Handler) {
  if (listeners.find((listener) => listener.type === type)) {
    devLogger(`[Client] Listener "${type}" already exists`, 'warn')
    return
  }

  listeners.push({ type, handler })
}

// ***
// INSTANCE (CAN BE RECREATED)
// ***
let authWorker: Worker | null = null
let isLeaderTab = false
let leaderCheckInterval: ReturnType<typeof setInterval> | null = null

const LEADER_TAB_KEY = `auth-worker-leader-tab-${import.meta.env.VITE_APP_ENV}`
/**
 * Max time (ms) a leader tab is considered active.
 * If not refreshed within this period, other tabs can take over leadership.
 */
const LEADER_TTL = 5000
/**
 * Interval (ms) to check if leader tab is still active.
 * If leader tab is not active, other tabs can take over leadership.
 */
const CHECK_INTERVAL = 2000

const broadcast = new BroadcastChannel('auth-sync')

// ***
// TAB LEADER SELECTION TO SYNC INTERVALS
// ***

function setupBroadcastListener() {
  broadcast.addEventListener('message', (event: MessageEvent<WorkerMessage>) => {
    const { type } = event.data

    if (type === 'checkTokenExpiration' && !isLeaderTab) {
      listeners.forEach((listener) => {
        if (listener.type === type) {
          try {
            listener.handler(event.data)
          } catch (error) {
            devLogger(`[Client] Error en listener "${type}" (broadcast)`, 'error', error)
          }
        }
      })
    }

    if (type === 'userSessionLogout') {
      listeners.forEach((listener) => {
        if (listener.type === type) {
          try {
            listener.handler(event.data)
          } catch (error) {
            devLogger(`[Client] Error en listener "${type}" (logout)`, 'error', error)
          }
        }
      })
    }
  })
}

function startLeaderHeartbeat() {
  // Keep leader tab "alive" as leader
  if (!isLeaderTab) return

  setInterval(() => {
    localStorage.setItem(LEADER_TAB_KEY, String(Date.now()))
  }, 4000) // less than LEADER_TTL
}

function electLeaderTab(worker: Worker) {
  const now = Date.now()
  const existing = Number(localStorage.getItem(LEADER_TAB_KEY) || 0)
  const isExpired = !existing || now - existing > LEADER_TTL

  if (!isExpired) {
    devLogger('[Client] Another tab is already leader, will sync via broadcast', 'info')
    return
  }

  localStorage.setItem(LEADER_TAB_KEY, String(now))

  setTimeout(() => {
    const current = Number(localStorage.getItem(LEADER_TAB_KEY) || 0)

    if (current === now) {
      isLeaderTab = true
      devLogger('[Client] Became leader tab for auth-worker', 'info')
      worker.postMessage({ type: 'startTokenExpirationCheck' })
      startLeaderHeartbeat()
    } else {
      devLogger('[Client] Another tab became leader in race condition', 'info')
    }
  }, 100)

  window.addEventListener('beforeunload', () => {
    const current = Number(localStorage.getItem(LEADER_TAB_KEY) || 0)
    if (isLeaderTab && current === now) {
      localStorage.removeItem(LEADER_TAB_KEY)
    }
  })
}

function startLeaderWatcher(worker: Worker) {
  leaderCheckInterval = setInterval(() => {
    if (isLeaderTab) return

    const lastLeader = Number(localStorage.getItem(LEADER_TAB_KEY) || 0)
    const now = Date.now()

    const isExpired = !lastLeader || (now - lastLeader) > LEADER_TTL

    if (isExpired) {
      devLogger('[Client] Leader tab expired. Trying to claim leadership...', 'info')
      electLeaderTab(worker)
    }
  }, CHECK_INTERVAL)
}

function stopLeaderWatcher() {
  if (leaderCheckInterval) {
    clearInterval(leaderCheckInterval)
    leaderCheckInterval = null
  }
}

// ***
// CREATE WORKER
// ***

export function createAuthWorker(): Worker {
  if (authWorker) return authWorker

  authWorker = new AuthWorkerConstructor({
    name: `cluber-auth-shared-worker-${import.meta.env.VITE_APP_ENV}`,
  })

  authWorker.addEventListener('message', (event: MessageEvent<WorkerMessage>) => {
    const { type } = event.data

    listeners.forEach((listener) => {
      if (listener.type === type) {
        try {
          listener.handler(event.data)
        } catch (error) {
          devLogger(`[Client] Error en listener "${type}"`, 'error', error)
        }
      }
    })
  })

  setupBroadcastListener()
  electLeaderTab(authWorker)
  startLeaderWatcher(authWorker)

  return authWorker
}

export function getAuthWorker() {
  return authWorker
}

export function destroyAuthWorker() {
  try {
    authWorker?.terminate()
  } catch (error) {
    devLogger('[Client] Error destroying auth worker', 'error', error)
  }
  authWorker = null
  isLeaderTab = false
  stopLeaderWatcher()
}
